import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

export interface MarinaPriceRule {
  id?: string;
  ruleCategory: 'BERTHING' | 'SERVICE' | 'OTHER';
  serviceCode?: string;
  stayUnit: 'HOUR' | 'DAY' | 'NIGHT' | 'WEEK' | 'MONTH' | 'SEASON' | 'VISIT';
  stayUnitQuantity: number;
  chargeBasis: 'FLAT' | 'PER_LENGTH_M' | 'PER_BEAM_M' | 'PER_AREA_SQM' | 'PER_PERSON' | 'PER_UNIT';
  priceAmount: number;
  contractPriceAmount?: number;
  currencyCode: string;
  minLengthM?: number;
  maxLengthM?: number;
  minBeamM?: number;
  maxBeamM?: number;
  minDraughtM?: number;
  maxDraughtM?: number;
  minDurationDays?: number;
  maxDurationDays?: number;
  validFrom?: string;
  validTo?: string;
  isDefault?: boolean;
  isTaxIncluded?: boolean;
  notes?: string;
  priceListId: string;
  priceListName?: string;
  clientId: string;
  quayId?: string;
  berthId?: string;
  seasonId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUpdateMarinaPriceRuleDto {
  ruleCategory: 'BERTHING' | 'SERVICE' | 'OTHER';
  serviceCode?: string;
  stayUnit: 'HOUR' | 'DAY' | 'NIGHT' | 'WEEK' | 'MONTH' | 'SEASON' | 'VISIT';
  stayUnitQuantity: number;
  chargeBasis: 'FLAT' | 'PER_LENGTH_M' | 'PER_BEAM_M' | 'PER_AREA_SQM' | 'PER_PERSON' | 'PER_UNIT';
  priceAmount: number;
  contractPriceAmount?: number;
  currencyCode: string;
  minLengthM?: number;
  maxLengthM?: number;
  minBeamM?: number;
  maxBeamM?: number;
  minDraughtM?: number;
  maxDraughtM?: number;
  minDurationDays?: number;
  maxDurationDays?: number;
  validFrom?: string;
  validTo?: string;
  isDefault?: boolean;
  isTaxIncluded?: boolean;
  notes?: string;
  priceListId: string;
  clientId: string;
  quayId?: string;
  berthId?: string;
  seasonId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MarinaPriceRuleService {
  private apiUrl = '/api/v1/price-rules';

  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    console.log('[MarinaPriceRuleService] Service initialized with WebSocket support');
  }

  /**
   * Get all price rules
   */
  getPriceRules(): Observable<MarinaPriceRule[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceRule[]>(this.apiUrl, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price rules:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get price rule by ID
   */
  getPriceRuleById(id: string): Observable<MarinaPriceRule> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceRule>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price rule by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get price rules by price list ID
   */
  getPriceRulesByPriceListId(priceListId: string): Observable<MarinaPriceRule[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceRule[]>(`${this.apiUrl}/price-list/${priceListId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price rules by price list ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new price rule
   */
  createPriceRule(priceRule: CreateUpdateMarinaPriceRuleDto): Observable<MarinaPriceRule> {
    const headers = this.getAuthHeaders();

    return this.http.post<MarinaPriceRule>(this.apiUrl, priceRule, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating price rule:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing price rule
   */
  updatePriceRule(id: string, priceRule: Partial<CreateUpdateMarinaPriceRuleDto>): Observable<MarinaPriceRule> {
    const headers = this.getAuthHeaders();

    return this.http.put<MarinaPriceRule>(`${this.apiUrl}/${id}`, priceRule, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating price rule:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a price rule
   */
  deletePriceRule(id: string): Observable<void> {
    const headers = this.getAuthHeaders();

    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting price rule:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');

    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      // Clear stored credentials and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }

  /**
   * WebSocket Integration Methods
   */

  /**
   * Get observable for real-time price rule updates from WebSocket (create/update)
   */
  getRealtimePriceRuleUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.MarinaPriceRule`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time price rule deletions from WebSocket
   */
  getRealtimePriceRuleDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.MarinaPriceRule`)
    );
    return merge(...observables);
  }
}

